import React from 'react';
import { Link } from 'react-router-dom';
import { FaStar, FaCheck, FaTimes, FaArrowRight } from 'react-icons/fa';
import SEO from '../components/SEO';
import { generateJSONLD } from '../utils/seo';

const Comparison: React.FC = () => {
  const offers = [
    {
      id: 'casinocasino',
      name: 'CasinoCasino',
      bonus: '100% up to £500 + 50 Spins',
      rating: 4.8,
      minDeposit: '£10',
      wagering: '35x',
      payoutTime: '24-48h',
      license: 'UKGC',
      features: ['Live Casino', 'Mobile App', 'VIP Program', '24/7 Support'],
      link: '/offers/casinocasino',
      image: '/images/offers/casinocasino.webp',
      url: 'https://www.casinocasino.com',
    },
    {
      id: 'mrplay',
      name: 'MrPlay',
      bonus: '200% up to £200 + 100 Spins',
      rating: 4.7,
      minDeposit: '£20',
      wagering: '30x',
      payoutTime: '12-24h',
      license: 'UKGC',
      features: ['Live Casino', 'Mobile App', 'VIP Program'],
      link: '/offers/mrplay',
      image: '/images/offers/mrplay.webp',
      url: 'https://www.mrplay.com/',
    },
    {
      id: 'partycasino',
      name: 'PartyCasino',
      bonus: '150% up to £300 + 75 Spins',
      rating: 4.9,
      minDeposit: '£10',
      wagering: '35x',
      payoutTime: '24-48h',
      license: 'UKGC',
      features: ['Live Casino', 'Mobile App', 'VIP Program', '24/7 Support', 'Cashback'],
      link: '/offers/partycasino',
      image: '/images/offers/partycasino.webp',
      url: 'https://www.partycasino.com/',
    },
  ];

  const allFeatures = Array.from(
    new Set(offers.flatMap((offer) => offer.features))
  );

  const schema = generateJSONLD({
    type: 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://gameobsidian.com',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Comparison',
        item: 'https://gameobsidian.com/comparison',
      },
    ],
  });

  return (
    <>
      <SEO
        title="Compare UK Casino Offers"
        description="Compare the best UK casino offers side-by-side. Compare bonuses, features, ratings, and more to find your perfect match."
        url="/comparison"
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: schema }}
      />

      <div className="min-h-screen pt-20 pb-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-6xl font-bold mb-4">
              <span className="text-gradient">Compare Casino Offers</span>
            </h1>
            <p className="text-xl text-dark-muted max-w-3xl mx-auto">
              Side-by-side comparison of the top UK casino offers. Find the best bonuses, features, and terms that suit your preferences.
            </p>
          </div>

          <div className="overflow-x-auto">
            <div className="glass-effect rounded-2xl p-6 min-w-[1000px]">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-dark-border">
                    <th className="text-left p-4 font-semibold">Casino</th>
                    <th className="text-left p-4 font-semibold">Welcome Bonus</th>
                    <th className="text-left p-4 font-semibold">Rating</th>
                    <th className="text-left p-4 font-semibold">Min Deposit</th>
                    <th className="text-left p-4 font-semibold">Wagering</th>
                    <th className="text-left p-4 font-semibold">Payout Time</th>
                    <th className="text-left p-4 font-semibold">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {offers.map((offer, index) => (
                    <tr
                      key={offer.id}
                      className="border-b border-dark-border hover:bg-dark-card/50 transition-colors"
                    >
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <img
                            src={offer.image}
                            alt={offer.name}
                            className="w-16 h-16 object-contain rounded-lg"
                            width="64"
                            height="64"
                            loading="lazy"
                            decoding="async"
                          />
                          <div>
                            <div className="font-bold text-lg">{offer.name}</div>
                            <div className="text-sm text-dark-muted">{offer.license}</div>
                          </div>
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="font-semibold text-accent-primary">
                          {offer.bonus}
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-1">
                          <FaStar className="text-accent-gold" />
                          <span className="font-semibold">{offer.rating}</span>
                        </div>
                      </td>
                      <td className="p-4">{offer.minDeposit}</td>
                      <td className="p-4">{offer.wagering}</td>
                      <td className="p-4">{offer.payoutTime}</td>
                      <td className="p-4">
                        <div className="flex flex-col gap-2">
                          <a
                            href={offer.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-accent-primary to-accent-secondary hover:opacity-90 rounded-lg font-semibold transition-opacity text-sm"
                          >
                            Play Now
                            <FaArrowRight className="text-xs" />
                          </a>
                          <Link
                            to={offer.link}
                            className="inline-flex items-center justify-center gap-2 px-4 py-2 glass-effect hover:bg-accent-primary/20 rounded-lg font-semibold transition-all text-xs"
                          >
                            Review
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Features Comparison */}
          <div className="mt-12">
            <h2 className="text-3xl font-bold mb-8 text-center">
              <span className="text-gradient">Features Comparison</span>
            </h2>
            <div className="glass-effect rounded-2xl p-6 overflow-x-auto">
              <table className="w-full min-w-[800px]">
                <thead>
                  <tr className="border-b border-dark-border">
                    <th className="text-left p-4 font-semibold">Feature</th>
                    {offers.map((offer) => (
                      <th key={offer.id} className="text-center p-4 font-semibold">
                        {offer.name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {allFeatures.map((feature) => (
                    <tr
                      key={feature}
                      className="border-b border-dark-border hover:bg-dark-card/50 transition-colors"
                    >
                      <td className="p-4 font-semibold">{feature}</td>
                      {offers.map((offer) => (
                        <td key={offer.id} className="p-4 text-center">
                          {offer.features.includes(feature) ? (
                            <FaCheck className="text-green-500 mx-auto text-xl" />
                          ) : (
                            <FaTimes className="text-red-500 mx-auto text-xl" />
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* CTA Section */}
          <div className="mt-12 text-center">
            <div className="glass-effect rounded-2xl p-8 inline-block">
              <h3 className="text-2xl font-bold mb-4">
                Ready to Claim Your Bonus?
              </h3>
              <p className="text-dark-muted mb-6">
                Choose your preferred casino and start playing with exclusive bonuses today
              </p>
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-accent-primary to-accent-secondary hover:opacity-90 rounded-lg font-semibold transition-opacity"
              >
                View All Offers
                <FaArrowRight />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Comparison;

